import { Link } from "react-router-dom";
import useAdminOperation from "../hooks/useAdminOperation";
import "./AdminSecondary.css";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Trash2, RefreshCw, Upload, Star, GripVertical } from "lucide-react";
import { api, formatApiError, API, getToken } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

const EMPTY_FORM = {
  title: "",
  description: "",
  image_url: "",
  categories: [],
  product_type: "other",
  initial_quantity: "0",
  height: "",
  length: "",
  newCategory: "",
  selectedCategory: "",
};

const isBrokenGalleryItem = (item = {}) => {
  const title = String(item.title || "").toLowerCase();
  const image = String(item.image_url || item.image || "").toLowerCase();

  return title.includes("test studio sign")
    || title.includes("gallery reorder")
    || title.includes("sample")
    || image.includes("assets/none")
    || image.includes("example.com")
    || image.includes("none");
};

const sanitizeGalleryItems = (items = []) => items.filter((item) => !isBrokenGalleryItem(item));

const normalizeGalleryItem = (item) => ({
  ...item,
  id: item.id || item._id || `${item.title}-${item.image_url || item.image}`,
  title: item.title || "Untitled piece",
  description: item.description || "",
  image_url: item.image_url || item.image || "",
  categories: Array.isArray(item.categories) ? item.categories : [item.category].filter(Boolean),
  initial_quantity: item.initial_quantity ?? item.on_hand ?? 0,
});

export default function AdminGallery() {
  const beginOperation = useAdminOperation();
  const [items, setItems] = useState([]);
  const [savedOrderIds, setSavedOrderIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [activeCategoryFilter, setActiveCategoryFilter] = useState("All");
  const [activeProductTypeFilter, setActiveProductTypeFilter] = useState("All");
  const [selectedItem, setSelectedItem] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editorForm, setEditorForm] = useState(EMPTY_FORM);
  const [form, setForm] = useState(EMPTY_FORM);
  const [uploading, setUploading] = useState(false);
  const [featuredIds, setFeaturedIds] = useState([]);
  const [featuredSaving, setFeaturedSaving] = useState(false);
  const [orderSaving, setOrderSaving] = useState(false);
  const [draggedItemId, setDraggedItemId] = useState(null);
  const fileInputRef = useRef(null);
  const editorFileInputRef = useRef(null);

  const loadSequence = useRef(0);
  const loadItems = useCallback(async () => {
    const sequence = ++loadSequence.current;
    const sessionCurrent = beginOperation();
    const operationCurrent = () => sessionCurrent() && sequence === loadSequence.current;
    if (!operationCurrent()) return;
    setLoading(true);
    try {
      const res = await api.get("/admin/gallery");
      if (!operationCurrent()) return;
      const nextItems = sanitizeGalleryItems((res.data || []).map(normalizeGalleryItem));
      setItems(nextItems);
      setSavedOrderIds(nextItems.map((item) => item.id));
      setLoadError("");
    } catch (err) {
      if (!operationCurrent()) return;
      const errMessage = formatApiError(err);
      setItems([]);
      setLoadError(errMessage);
      toast.error(errMessage);
    } finally {
      if (operationCurrent()) {
      setLoading(false);
      }
    }
  }, [beginOperation]);

  useEffect(() => {
    const operationCurrent = beginOperation();
    loadItems();
    api
      .get("/admin/featured")
      .then((res) => { if (operationCurrent()) setFeaturedIds(res.data?.gallery_ids || []); })
      .catch((err) => { if (operationCurrent()) toast.error(formatApiError(err)); });
  }, [loadItems, beginOperation]);

  const saveFeatured = async () => {
    const operationCurrent = beginOperation();
    if (!operationCurrent()) return;
    setFeaturedSaving(true);
    try {
      const response = await api.put("/admin/featured", {
        gallery_ids: featuredIds,
      });
      if (!operationCurrent()) return;
      setFeaturedIds(response.data.gallery_ids || []);
      toast.success("Featured slideshow saved.");
    } catch (err) {
      if (!operationCurrent()) return;
      toast.error(formatApiError(err));
    } finally {
      if (operationCurrent()) {
      setFeaturedSaving(false);
      }
    }
  };

  const toggleFeatured = (item) => {
    const isFeatured = featuredIds.includes(item.id);
    if (isFeatured) {
      setFeaturedIds((current) => current.filter((id) => id !== item.id));
      return;
    }
    if (featuredIds.length >= 5) {
      toast.error("Featured slideshow is limited to 5 pieces. Remove one first.");
      return;
    }
    setFeaturedIds((current) => [...current, item.id]);
  };

  const moveFeatured = (id, direction) => {
    setFeaturedIds((current) => {
      const index = current.indexOf(id);
      const target = direction === "up" ? index - 1 : index + 1;
      if (index < 0 || target < 0 || target >= current.length) return current;
      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const featuredItems = featuredIds
    .map((id) => items.find((item) => item.id === id))
    .filter(Boolean);

  const uploadGalleryImage = async (event, target = "new") => {
    const operationCurrent = beginOperation();
    if (!operationCurrent()) return;
    const file = event.target.files?.[0];
    if (!file) return;
    const data = new FormData();
    data.append("file", file);
    setUploading(true);
    try {
      const res = await fetch(`${API}/admin/gallery/upload`, {
        method: "POST",
        body: data,
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (!operationCurrent()) return;
      const payload = await res.json();
      if (!operationCurrent()) return;
      if (!res.ok) {
        throw new Error(payload?.detail || "Image upload failed");
      }
      const uploadedUrl = `${API.replace(/\/api$/, "")}/api/gallery/assets/${payload.filename}`;
      if (target === "editor") {
        applyEditorField("image_url", uploadedUrl);
      } else {
        applyField("image_url", uploadedUrl);
      }
      toast.success(target === "editor" ? "Current image replaced." : "New image uploaded.");
    } catch (err) {
      if (!operationCurrent()) return;
      toast.error(err?.message || formatApiError(err));
    } finally {
      if (operationCurrent()) {
      setUploading(false);
      event.target.value = "";
      }
    }
  };

  const categoryList = useMemo(
    () =>
      Array.from(
        new Set(
          items.flatMap((item) => item.categories || [])
            .map((category) => String(category).trim())
            .filter(Boolean)
        )
      ),
    [items]
  );

  const applyField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const addCategory = (value) => {
    const item = String(value || "").trim();
    if (!item) return;
    setForm((prev) => {
      if (prev.categories.includes(item)) return prev;
      return { ...prev, categories: [...prev.categories, item], newCategory: "", selectedCategory: "" };
    });
  };

  const removeCategory = (category) => {
    setForm((prev) => ({ ...prev, categories: prev.categories.filter((item) => item !== category), selectedCategory: "" }));
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
  };

  const resetEditor = () => {
    setSelectedItem(null);
    setEditing(false);
    setEditorForm(EMPTY_FORM);
  };

  const openEditor = (item) => {
    setSelectedItem(item);
    setEditing(true);
    setEditorForm({
      title: item.title || "",
      description: item.description || "",
      image_url: item.image_url || item.image || "",
      categories: Array.isArray(item.categories) ? item.categories : [],
      product_type: item.product_type || "other",
      initial_quantity: String(item.initial_quantity ?? 0),
      height: item.height == null ? "" : String(item.height),
      length: item.length == null ? "" : String(item.length),
      newCategory: "",
      selectedCategory: "",
    });
  };

  const applyEditorField = (key, value) => {
    setEditorForm((prev) => ({ ...prev, [key]: value }));
  };

  const addEditorCategory = (value) => {
    const item = String(value || "").trim();
    if (!item) return;
    setEditorForm((prev) => {
      if (prev.categories.includes(item)) return prev;
      return { ...prev, categories: [...prev.categories, item], newCategory: "", selectedCategory: "" };
    });
  };

  const removeEditorCategory = (category) => {
    setEditorForm((prev) => ({ ...prev, categories: prev.categories.filter((item) => item !== category), selectedCategory: "" }));
  };

  const submitForm = async (e) => {
    const operationCurrent = beginOperation();
    if (!operationCurrent()) return;
    e.preventDefault();
    const title = form.title.trim();
    const description = form.description.trim();
    const image_url = form.image_url.trim();
    const categories = form.categories.map((item) => item.trim()).filter(Boolean);
    const initial_quantity = Number(form.initial_quantity);
    const height = form.height === "" ? null : Number(form.height);
    const length = form.length === "" ? null : Number(form.length);

    if (!title || !description || !image_url || categories.length === 0 || !Number.isSafeInteger(initial_quantity) || initial_quantity < 0 || (height !== null && (!Number.isFinite(height) || height <= 0)) || (length !== null && (!Number.isFinite(length) || length <= 0))) {
      toast.error("Please complete the title, description, image URL, and at least one category.");
      return;
    }

    setSaving(true);
    try {
      await api.post("/admin/gallery", { title, description, image_url, categories, product_type: form.product_type, initial_quantity, height, length });
      if (!operationCurrent()) return;
      toast.success("Gallery item added.");
      resetForm();
      await loadItems();
      if (!operationCurrent()) return;
    } catch (err) {
      if (!operationCurrent()) return;
      toast.error(formatApiError(err));
    } finally {
      if (operationCurrent()) {
      setSaving(false);
      }
    }
  };

  const filteredItems = items.filter((item) => (
    (activeCategoryFilter === "All" || (item.categories || []).includes(activeCategoryFilter))
    && (activeProductTypeFilter === "All" || (item.product_type || "other") === activeProductTypeFilter)
  ));
  const currentOrderIds = items.map((item) => item.id);
  const orderIsDirty = currentOrderIds.length !== savedOrderIds.length
    || currentOrderIds.some((id, index) => id !== savedOrderIds[index]);

  const stageItemMove = (itemId, targetItemId) => {
    if (!itemId || !targetItemId || itemId === targetItemId) return;
    const visible = filteredItems;
    const fromIndex = visible.findIndex((item) => item.id === itemId);
    const targetIndex = visible.findIndex((item) => item.id === targetItemId);
    if (fromIndex < 0 || targetIndex < 0 || fromIndex === targetIndex) return;
    const nextVisible = [...visible];
    [nextVisible[fromIndex], nextVisible[targetIndex]] = [nextVisible[targetIndex], nextVisible[fromIndex]];
    let visibleIndex = 0;
    setItems((current) => current.map((item) => (
      (activeCategoryFilter === "All" || (item.categories || []).includes(activeCategoryFilter))
        && (activeProductTypeFilter === "All" || (item.product_type || "other") === activeProductTypeFilter)
        ? nextVisible[visibleIndex++]
        : item
    )));
  };

  const moveItem = (item, direction) => {
    const index = filteredItems.findIndex((entry) => entry.id === item.id);
    const target = direction === "up" ? filteredItems[index - 1] : filteredItems[index + 1];
    if (target) stageItemMove(item.id, target.id);
  };

  const saveGalleryOrder = async () => {
    const operationCurrent = beginOperation();
    if (!operationCurrent()) return;
    if (!orderIsDirty) return;
    setOrderSaving(true);
    try {
      const ordered_ids = items.map((item) => item.id);
      const response = await api.put("/admin/gallery/reorder", { ordered_ids });
      if (!operationCurrent()) return;
      const persistedIds = response.data?.ordered_ids || ordered_ids;
      const itemById = new Map(items.map((item) => [item.id, item]));
      const persistedItems = persistedIds.map((id) => itemById.get(id)).filter(Boolean);
      setItems(persistedItems);
      setSavedOrderIds(persistedItems.map((item) => item.id));
      toast.success("Gallery order saved.");
    } catch (err) {
      if (!operationCurrent()) return;
      toast.error(formatApiError(err));
    } finally {
      if (operationCurrent()) {
      setOrderSaving(false);
      }
    }
  };

  const deleteItem = async (item) => {
    const operationCurrent = beginOperation();
    if (!operationCurrent()) return;
    const ok = window.confirm(`Delete "${item.title}" from the gallery?`);
    if (!ok) return;
    try {
      await api.delete(`/admin/gallery/${item.id}`);
      if (!operationCurrent()) return;
      toast.success("Gallery item deleted.");
      await loadItems();
      if (!operationCurrent()) return;
    } catch (err) {
      if (!operationCurrent()) return;
      toast.error(formatApiError(err));
    }
  };

  const saveEditedItem = async (e) => {
    const operationCurrent = beginOperation();
    if (!operationCurrent()) return;
    e.preventDefault();
    if (!selectedItem) return;
    const title = editorForm.title.trim();
    const description = editorForm.description.trim();
    const image_url = editorForm.image_url.trim();
    const categories = editorForm.categories.map((item) => item.trim()).filter(Boolean);
    const initial_quantity = Number(editorForm.initial_quantity);
    const height = editorForm.height === "" ? null : Number(editorForm.height);
    const length = editorForm.length === "" ? null : Number(editorForm.length);

    if (!title || !description || !image_url || categories.length === 0 || !Number.isSafeInteger(initial_quantity) || initial_quantity < 0 || (height !== null && (!Number.isFinite(height) || height <= 0)) || (length !== null && (!Number.isFinite(length) || length <= 0))) {
      toast.error("Please complete the title, description, image URL, and at least one category.");
      return;
    }

    setSaving(true);
    try {
      const res = await api.patch(`/admin/gallery/${selectedItem.id}`, { title, description, image_url, categories, product_type: editorForm.product_type, initial_quantity, height, length });
      if (!operationCurrent()) return;
      const updatedItem = normalizeGalleryItem(res.data || {});
      setItems((current) => current.map((entry) => (entry.id === selectedItem.id ? updatedItem : entry)));
      setSelectedItem(updatedItem);
      setEditorForm({
        title: updatedItem.title || "",
        description: updatedItem.description || "",
        image_url: updatedItem.image_url || "",
        categories: updatedItem.categories || [],
        product_type: updatedItem.product_type || editorForm.product_type,
        initial_quantity: String(updatedItem.initial_quantity ?? initial_quantity),
        height: updatedItem.height == null ? (height == null ? "" : String(height)) : String(updatedItem.height),
        length: updatedItem.length == null ? (length == null ? "" : String(length)) : String(updatedItem.length),
        newCategory: "",
        selectedCategory: "",
      });
      toast.success("Gallery item updated.");
    } catch (err) {
      if (!operationCurrent()) return;
      toast.error(formatApiError(err));
    } finally {
      if (operationCurrent()) {
      setSaving(false);
      }
    }
  };

  return (
    <div className="bbw-admin-secondary min-h-screen" data-testid="admin-gallery-page">
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-10 pb-6 flex flex-wrap gap-4 items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.28em] text-[#D8A34B] mb-2">Admin Dashboard</p>
          <h1 className="font-display text-4xl text-[#F5F0E6]">Manage Gallery</h1>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button onClick={loadItems} variant="ghost" className="text-stone-300 hover:text-stone-100">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>

        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-6">
        <div className="rounded-2xl border border-[#26C6D5]/25 bg-[#26C6D5]/5 p-5" data-testid="featured-manager">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#26C6D5]">
                Homepage
              </p>
              <h2 className="font-display text-2xl text-stone-100">
                Featured slideshow
              </h2>
              <p className="mt-1 text-sm text-stone-400">
                <span className="font-semibold text-stone-200" data-testid="featured-count">{featuredIds.length} / 5 selected</span>
                {" · "}star pieces in the gallery list below to add or remove them.
              </p>
            </div>
            <Button
              type="button"
              onClick={saveFeatured}
              disabled={featuredSaving}
              className="bg-[#26C6D5] text-[#15191C]"
            >
              {featuredSaving ? "Saving..." : "Save featured"}
            </Button>
          </div>
          {featuredItems.length === 0 ? (
            <p className="mt-4 text-sm text-stone-400" data-testid="featured-empty">
              No featured pieces selected. Star up to five gallery items below.
            </p>
          ) : (
            <div className="mt-4 space-y-2" data-testid="featured-selected-list">
              {featuredItems.map((item, index) => (
                <article
                  key={item.id}
                  className="admin-featured-row flex items-center gap-3 rounded-lg border border-stone-100/10 bg-zinc-950/40 p-2"
                >
                  <img
                    src={item.image_url}
                    alt={item.title}
                    className="h-10 w-10 shrink-0 rounded object-cover"
                  />
                  <span className="min-w-0 flex-1 truncate text-sm text-stone-200">
                    <span className="mr-2 text-xs text-stone-500">#{index + 1}</span>
                    {item.title}
                  </span>
                  <div className="flex shrink-0 gap-1">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => moveFeatured(item.id, "up")}
                      disabled={index === 0}
                      className="h-8 w-8 bg-transparent border-stone-100/15 text-stone-200 p-0 hover:bg-stone-100/5 disabled:opacity-40"
                      aria-label={`Move ${item.title} earlier in the featured slideshow`}
                    >
                      ↑
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => moveFeatured(item.id, "down")}
                      disabled={index === featuredItems.length - 1}
                      className="h-8 w-8 bg-transparent border-stone-100/15 text-stone-200 p-0 hover:bg-stone-100/5 disabled:opacity-40"
                      aria-label={`Move ${item.title} later in the featured slideshow`}
                    >
                      ↓
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => toggleFeatured(item)}
                      className="h-8 w-8 bg-transparent border-[#D8A34B]/40 p-0 text-[#D8A34B] hover:bg-[#D8A34B]/10"
                      aria-label={`Remove ${item.title} from featured slideshow`}
                    >
                      <Star className="h-4 w-4 fill-current" />
                    </Button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-10 grid grid-cols-1 lg:grid-cols-[1.05fr_1.4fr] gap-6">
        {loadError && <div className="lg:col-span-2 rounded-xl border border-rose-400/30 bg-rose-950/20 p-4 text-rose-200">Unable to load the gallery from the server. <button type="button" onClick={loadItems} className="underline">Retry</button></div>}
        <details className="admin-gallery-create admin-secondary-editor"><summary>Add a gallery piece<span>Photo, product details and stock</span></summary>
        <form onSubmit={submitForm} className="rounded-2xl border border-stone-100/10 bg-zinc-900/70 p-6 space-y-5">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-2xl text-stone-100">Add New Gallery Piece</h2>
            <span className="rounded-full border border-[#26C6D5]/40 bg-[#26C6D5]/10 px-2 py-1 text-[10px] uppercase tracking-[0.2em] text-[#26C6D5]">
              Live
            </span>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-sm text-stone-200">Title</label>
              <Input value={form.title} onChange={(e) => applyField("title", e.target.value)} placeholder="Sunflower Porch Welcome" className="mt-1" />
            </div>

            <div>
              <label className="text-sm text-stone-200">Image</label>
              <div className="mt-1 flex gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={(e) => uploadGalleryImage(e, "new")}
                />
                <Button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  variant="outline"
                  className="w-full bg-transparent border-[#26C6D5]/40 text-[#26C6D5] hover:bg-[#26C6D5]/10"
                  disabled={uploading}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? "Uploading..." : "Upload new image"}
                </Button>
              </div>
              {form.image_url ? (
                <div className="mt-3 overflow-hidden rounded-xl border border-stone-100/10 bg-zinc-950/60 p-2">
                  <img src={form.image_url} alt="Selected portfolio preview" className="max-h-28 w-full object-contain rounded-md bg-zinc-950" />
                  <p className="mt-2 text-[10px] break-all text-stone-300">{form.image_url}</p>
                </div>
              ) : null}
            </div>

            <div>
              <label className="text-sm text-stone-200">Categories</label>
              <div className="mt-2 space-y-3">
                <div className="admin-secondary-inline-fields flex gap-2">
                  <select
                    value={form.selectedCategory}
                    onChange={(e) => applyField("selectedCategory", e.target.value)}
                    className="flex h-10 w-full rounded-md border border-stone-100/15 bg-zinc-950 px-3 py-2 text-sm text-stone-100 outline-none focus:border-[#26C6D5]"
                  >
                    <option value="">Choose an existing category</option>
                    {categoryList.map((category) => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                  <Button
                    type="button"
                    onClick={() => addCategory(form.selectedCategory)}
                    variant="outline"
                    className="bg-transparent border-[#26C6D5]/40 text-[#26C6D5] hover:bg-[#26C6D5]/10"
                  >
                    Add
                  </Button>
                </div>

                <div className="admin-secondary-inline-fields flex gap-2">
                  <Input
                    value={form.newCategory}
                    onChange={(e) => applyField("newCategory", e.target.value)}
                    placeholder="Add a new category"
                    className="mt-0"
                  />
                  <Button
                    type="button"
                    onClick={() => addCategory(form.newCategory)}
                    variant="outline"
                    className="bg-transparent border-[#D8A34B]/40 text-[#D8A34B] hover:bg-[#D8A34B]/10"
                  >
                    New
                  </Button>
                </div>

                {form.categories.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {form.categories.map((category) => (
                      <span key={category} className="inline-flex items-center gap-2 rounded-full border border-[#26C6D5]/35 bg-[#26C6D5]/10 px-2 py-1 text-[10px] uppercase tracking-[0.2em] text-[#26C6D5]">
                        {category}
                        <button
                          type="button"
                          onClick={() => removeCategory(category)}
                          className="text-[#8FE8F8] hover:text-white"
                          aria-label={`Remove ${category}`}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-stone-400">No categories selected yet.</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="text-sm text-stone-200">Product type</label>
                <select
                  value={form.product_type}
                  onChange={(e) => applyField("product_type", e.target.value)}
                  className="mt-1 flex h-10 w-full rounded-md border border-stone-100/15 bg-zinc-950 px-3 py-2 text-sm text-stone-100 outline-none focus:border-[#26C6D5]"
                >
                  <option value="round_sign">Round sign</option>
                  <option value="mason_jar">Mason Jar</option>
                  <option value="shadow_box">Shadow box</option>
                  <option value="swing">Swing</option>
                  <option value="other">Other / review</option>
                </select>
                <p className="mt-1 text-xs text-stone-400">A default price is applied for priced types and can be changed in Inventory.</p>
              </div>
              <div>
                <label className="text-sm text-stone-200">Starting quantity</label>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  value={form.initial_quantity}
                  onChange={(e) => applyField("initial_quantity", e.target.value)}
                  placeholder="0"
                  inputMode="numeric"
                  className="mt-1"
                />
                <p className="mt-1 text-xs text-stone-400">This becomes the linked product&apos;s on-hand inventory.</p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="text-sm text-stone-200">Height</label>
                <Input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={form.height}
                  onChange={(e) => applyField("height", e.target.value)}
                  placeholder="Height in inches"
                  inputMode="decimal"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm text-stone-200">Length</label>
                <Input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={form.length}
                  onChange={(e) => applyField("length", e.target.value)}
                  placeholder="Length in inches"
                  inputMode="decimal"
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <label className="text-sm text-stone-200">Description</label>
              <Textarea value={form.description} onChange={(e) => applyField("description", e.target.value)} rows={5} placeholder="Short description of the piece and its style." className="mt-1" />
            </div>
          </div>

          <Button type="submit" disabled={saving} className="w-full bg-[#26C6D5] hover:bg-[#1ab7d2] text-[#15191C] rounded-md">
            <ImagePlus className="w-4 h-4 mr-2" />
            {saving ? "Saving..." : "Add Gallery Item"}
          </Button>
        </form>
        </details>

        <div className="rounded-2xl border border-stone-100/10 bg-zinc-900/70 p-6">
          <h2 className="font-display text-2xl text-stone-100 mb-4">Current Categories</h2>
          {categoryList.length === 0 ? (
            <p className="text-stone-400">No categories have been added yet.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {['All', ...categoryList].map((category) => (
                <button
                  key={category}
                  type="button"
                  onClick={() => setActiveCategoryFilter(category)}
                  className={`rounded-full border px-2 py-1 text-[10px] uppercase tracking-[0.2em] transition ${
                    activeCategoryFilter === category
                      ? 'border-[#26C6D5] bg-[#26C6D5]/15 text-[#26C6D5]'
                      : 'border-[#D8A34B]/35 bg-[#D8A34B]/10 text-[#D8A34B] hover:border-[#26C6D5]/40 hover:text-[#F5F0E6]'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <label htmlFor="gallery-product-type-filter" className="text-xs uppercase tracking-[0.16em] text-stone-400">Product type</label>
            <select
              id="gallery-product-type-filter"
              value={activeProductTypeFilter}
              onChange={(event) => setActiveProductTypeFilter(event.target.value)}
              data-testid="gallery-product-type-filter"
              className="h-9 rounded-md border border-stone-100/15 bg-zinc-950 px-3 text-sm text-stone-100 outline-none focus:border-[#26C6D5]"
            >
              <option value="All">All product types</option>
              <option value="round_sign">Round sign</option>
              <option value="mason_jar">Mason Jar</option>
              <option value="shadow_box">Shadow box</option>
              <option value="swing">Swing</option>
              <option value="other">Other / review</option>
            </select>
          </div>

          <div className="mt-8">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="font-display text-xl text-stone-100">Gallery Items</h3>
                <p className="mt-1 text-xs text-stone-400">Filter by category or product type, then drag a piece or use the arrows to stage its position. Category tabs keep the same gallery order.</p>
              </div>
              <Button
                type="button"
                onClick={saveGalleryOrder}
                disabled={!orderIsDirty || orderSaving || loading}
                className="bg-[#26C6D5] text-[#15191C] disabled:opacity-40"
                data-testid="save-gallery-order"
              >
                {orderSaving ? "Saving order..." : orderIsDirty ? "Save gallery order" : "Order saved"}
              </Button>
            </div>
            {loading ? <p className="text-stone-400">Loading items...</p> : null}
            {!loading && filteredItems.length === 0 ? <p className="text-stone-400">No gallery items match this category yet.</p> : null}
            {!loading && filteredItems.length > 0 ? (
              <div className="space-y-3 max-h-[520px] overflow-y-auto pr-1">
                {filteredItems.map((item, index) => (
                  <article
                    key={item.id}
                    draggable={!orderSaving}
                    onDragStart={(event) => {
                      event.dataTransfer.effectAllowed = "move";
                      event.dataTransfer.setData("text/plain", item.id);
                      setDraggedItemId(item.id);
                    }}
                    onDragOver={(event) => { event.preventDefault(); event.dataTransfer.dropEffect = "move"; }}
                    onDrop={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      stageItemMove(event.dataTransfer.getData("text/plain") || draggedItemId, item.id);
                      setDraggedItemId(null);
                    }}
                    onDragEnd={() => setDraggedItemId(null)}
                    data-testid={`gallery-order-item-${item.id}`}
                    className={`rounded-xl border border-stone-100/10 bg-zinc-950/60 p-3 cursor-pointer hover:border-[#26C6D5]/30 ${draggedItemId === item.id ? "opacity-40" : ""}`}
                    onClick={() => openEditor(item)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex shrink-0 flex-col items-center gap-1 pt-1 text-stone-500" aria-label={`Reorder ${item.title}`}>
                        <GripVertical className="h-5 w-5" aria-hidden="true" />
                        <span className="text-[10px]">{index + 1}</span>
                      </div>
                      <img src={item.image_url} alt={item.title} className="h-16 w-16 object-cover rounded-md border border-stone-100/10" />
                      <div className="min-w-0 flex-1">
                        <p className="font-display text-lg text-stone-100">{item.title}</p>
                        <div className="mt-1 flex flex-wrap gap-2">
                          {(item.categories || []).map((category) => (
                            <span key={`${item.id}-${category}`} className="text-[9px] uppercase tracking-[0.15em] text-[#26C6D5]">
                              {category}
                            </span>
                          ))}
                        </div>
                        <p className="mt-2 text-sm text-stone-300 line-clamp-2">{item.description}</p>
                      </div>
                      <div className="flex shrink-0 flex-col gap-2" onClick={(e) => e.stopPropagation()}>
                        <div className="flex gap-1">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => toggleFeatured(item)}
                            className={`h-8 w-8 p-0 ${
                              featuredIds.includes(item.id)
                                ? "border-[#D8A34B]/60 bg-[#D8A34B]/15 text-[#D8A34B] hover:bg-[#D8A34B]/25"
                                : "bg-transparent border-stone-100/15 text-stone-400 hover:bg-stone-100/5 hover:text-[#D8A34B]"
                            }`}
                            aria-label={
                              featuredIds.includes(item.id)
                                ? `Remove ${item.title} from featured slideshow`
                                : `Add ${item.title} to featured slideshow`
                            }
                            aria-pressed={featuredIds.includes(item.id)}
                            data-testid={`featured-star-${item.id}`}
                          >
                            <Star className={`h-4 w-4 ${featuredIds.includes(item.id) ? "fill-current" : ""}`} />
                          </Button>
                          <Button type="button" variant="outline" onClick={() => moveItem(item, "up")} disabled={index === 0 || orderSaving} className="h-8 w-8 bg-transparent border-stone-100/15 text-stone-200 p-0 hover:bg-stone-100/5 disabled:opacity-40" aria-label={`Move ${item.title} up`}>
                            ↑
                          </Button>
                          <Button type="button" variant="outline" onClick={() => moveItem(item, "down")} disabled={index === filteredItems.length - 1 || orderSaving} className="h-8 w-8 bg-transparent border-stone-100/15 text-stone-200 p-0 hover:bg-stone-100/5 disabled:opacity-40" aria-label={`Move ${item.title} down`}>
                            ↓
                          </Button>
                        </div>
                        <Button type="button" variant="outline" onClick={() => deleteItem(item)} className="bg-transparent border-rose-500/30 text-rose-300 hover:bg-rose-500/10 shrink-0">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </section>

      {selectedItem && editing ? (
        <div className="admin-gallery-editor-overlay fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4" onClick={resetEditor}>
          <div className="admin-gallery-editor relative max-h-[calc(100vh-2rem)] w-full max-w-3xl overflow-y-auto rounded-2xl border border-stone-100/10 bg-zinc-950 p-4 sm:p-6" onClick={(e) => e.stopPropagation()}>
            <div className="admin-secondary-action-row flex items-center justify-between mb-4">
              <h2 className="font-display text-3xl text-stone-100">Edit Gallery Piece</h2>
              <button type="button" onClick={resetEditor} className="text-stone-400 hover:text-white">Close</button>
            </div>

            <div className="admin-gallery-pricing-handoff" role="note">
              <strong>Pricing and available sizes</strong>
              <p>Prices are managed in the linked product record, not in gallery images. Open its verified inventory record to review base and variant prices.</p>
              <Link to={`/admin/inventory?gallery=${encodeURIComponent(selectedItem.id)}#inventory-editor`}>Open product pricing →</Link>
            </div>
            <form onSubmit={saveEditedItem} className="space-y-4">
              <div>
                <label className="text-sm text-stone-200">Title</label>
                <Input value={editorForm.title} onChange={(e) => applyEditorField("title", e.target.value)} className="mt-1" />
              </div>

              <div>
                <label className="text-sm text-stone-200">Image</label>
                <div className="mt-1 flex gap-2">
                  <input
                    ref={editorFileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    onChange={(e) => uploadGalleryImage(e, "editor")}
                  />
                  <Button
                    type="button"
                    onClick={() => editorFileInputRef.current?.click()}
                    variant="outline"
                    className="w-full bg-transparent border-[#26C6D5]/40 text-[#26C6D5] hover:bg-[#26C6D5]/10"
                    disabled={uploading}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {uploading ? "Uploading..." : "Replace current image"}
                  </Button>
                </div>
                {editorForm.image_url ? (
                  <div className="mt-3 overflow-hidden rounded-xl border border-stone-100/10 bg-zinc-950/60 p-2">
                    <img src={editorForm.image_url} alt={editorForm.title || "Gallery preview"} className="max-h-32 w-full object-contain rounded-md bg-zinc-950" />
                    <p className="mt-2 text-[10px] break-all text-stone-300">{editorForm.image_url}</p>
                  </div>
                ) : null}
              </div>

              <div>
                <label className="text-sm text-stone-200">Categories</label>
                <div className="mt-2 space-y-3">
                  <div className="admin-secondary-inline-fields flex gap-2">
                    <select
                      value={editorForm.selectedCategory}
                      onChange={(e) => applyEditorField("selectedCategory", e.target.value)}
                      className="flex h-10 w-full rounded-md border border-stone-100/15 bg-zinc-950 px-3 py-2 text-sm text-stone-100 outline-none focus:border-[#26C6D5]"
                    >
                      <option value="">Choose an existing category</option>
                      {categoryList.map((category) => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                    <Button type="button" onClick={() => addEditorCategory(editorForm.selectedCategory)} variant="outline" className="bg-transparent border-[#26C6D5]/40 text-[#26C6D5] hover:bg-[#26C6D5]/10">
                      Add
                    </Button>
                  </div>

                  <div className="admin-secondary-inline-fields flex gap-2">
                    <Input value={editorForm.newCategory} onChange={(e) => applyEditorField("newCategory", e.target.value)} placeholder="Add a new category" className="mt-0" />
                    <Button type="button" onClick={() => addEditorCategory(editorForm.newCategory)} variant="outline" className="bg-transparent border-[#D8A34B]/40 text-[#D8A34B] hover:bg-[#D8A34B]/10">
                      New
                    </Button>
                  </div>

                  {editorForm.categories.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {editorForm.categories.map((category) => (
                        <span key={category} className="inline-flex items-center gap-2 rounded-full border border-[#26C6D5]/35 bg-[#26C6D5]/10 px-2 py-1 text-[10px] uppercase tracking-[0.2em] text-[#26C6D5]">
                          {category}
                          <button type="button" onClick={() => removeEditorCategory(category)} className="text-[#8FE8F8] hover:text-white" aria-label={`Remove ${category}`}>
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-stone-400">No categories selected yet.</p>
                  )}
                </div>
              </div>

              <div>
                <label className="text-sm text-stone-200">Description</label>
                <Textarea value={editorForm.description} onChange={(e) => applyEditorField("description", e.target.value)} rows={5} className="mt-1" />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-sm text-stone-200">Product type</label>
                  <select
                    value={editorForm.product_type}
                    onChange={(e) => applyEditorField("product_type", e.target.value)}
                    className="mt-1 flex h-10 w-full rounded-md border border-stone-100/15 bg-zinc-900 px-3 py-2 text-sm text-stone-100 outline-none focus:border-[#26C6D5]"
                  >
                    <option value="round_sign">Round sign</option>
                    <option value="mason_jar">Mason Jar</option>
                    <option value="shadow_box">Shadow box</option>
                    <option value="swing">Swing</option>
                    <option value="other">Other / review</option>
                  </select>
                  <p className="mt-1 text-xs text-stone-400">Changing type applies a default only when no price exists.</p>
                </div>
                <div>
                  <label className="text-sm text-stone-200">Quantity on hand</label>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={editorForm.initial_quantity}
                    onChange={(e) => applyEditorField("initial_quantity", e.target.value)}
                    inputMode="numeric"
                    className="mt-1"
                  />
                  <p className="mt-1 text-xs text-stone-400">Current stored quantity. Save to update inventory.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-sm text-stone-200">Height</label>
                  <Input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={editorForm.height}
                    onChange={(e) => applyEditorField("height", e.target.value)}
                    placeholder="Height in inches"
                    inputMode="decimal"
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm text-stone-200">Length</label>
                  <Input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={editorForm.length}
                    onChange={(e) => applyEditorField("length", e.target.value)}
                    placeholder="Length in inches"
                    inputMode="decimal"
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={resetEditor} className="bg-transparent border-stone-100/15 text-stone-200 hover:bg-stone-100/5">Cancel</Button>
                <Button type="submit" disabled={saving} className="bg-[#26C6D5] hover:bg-[#1ab7d2] text-[#15191C] rounded-md">{saving ? "Saving..." : "Save Changes"}</Button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}
